// 時間前市場 一括作成 (AC-BIN-006b / 006c) のプレビュー生成ロジック。
//
// design `design/bidding/intraday.html` `#intraday-bulk-add-modal` の
// 「コマ範囲 × エリア × 売/買 × 固定価格 × コマあたり MW」 で複数札を起こす入力に対し、
// GC 通過済コマを skip 対象として判定した行を返す。 既存札と重複するコマ (警告のみで作成は止めない) は
// 別の関数 (`intradayBulkDuplicateTimeCodes`) が返し、 比べる相手の札は `intradayBulkComparableBids` が選ぶ。
// 価格がフォーミュラの時は、 コマごとのプレビューから価格列の表示と確定の可否を決める。
// 確定時の永続化 (CreateBiddingIntradayOrder のループ実行) は呼び出し元の責務。
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import type { Side } from '@ebalance/proto/bfftypes/v1/side_pb'

import type { IntradayOrderRow } from '~/hooks/bidding/useListBiddingIntradayOrders'
import { fromProtoDate, isSameDay } from '~/models/dateTime'
import {
  type FeeAdjustmentViolation,
  type FeeAutoConsiderHint,
  type FeeAutoConsiderPreview,
  type FeeAutoConsiderTreatment,
  fallbackOffPreview,
  feeAutoConsiderHint,
  formatYenPerKwh,
} from '~/pages/IntradayBidding/models/feeAutoConsider'
import {
  isMarketOrderRow,
  isWithdrawnOrder,
} from '~/pages/IntradayBidding/models/intradayBidRow'
import {
  type FormulaPricePreviewResult,
  type FormulaPriceSubmitCheck,
  formulaPriceSubmitCheck,
} from '~/pages/IntradayBidding/models/intradayFormulaPricePreview'
import type { IntradayOrderCreatePrice } from '~/pages/IntradayBidding/models/intradayOrderWrite'
import {
  type IntradayTableStatus,
  composeIntradayTableStatus,
} from '~/pages/IntradayBidding/models/intradayTableStatus'

/** 一括作成フォームの確定値 (価格を除く。 価格は `IntradayBulkCreatePrice`)。 */
export type IntradayBulkCreateParams = {
  /** コマ範囲 (1〜48、 inclusive)。 昇順でなくても内部で正規化する。 */
  slotFrom: number
  slotTo: number
  areaCode: AreaCode
  direction: Side
  /** コマあたり入札量 (MW 表記、 文字列。 単位移行は issue #2708)。 */
  volumeMw: string
  /** 発注主体区分。 生成札全件に同一適用する (issue #5366)。 */
  issuerType: LicenseType
}

/**
 * 全札に同じく使う価格 (AC-BIN-006b: 固定価格 / フォーミュラ)。 作成 request の価格と同じ形で、
 * 一括作成は成行に非対応 (AC-BIN-004d)。 フォーミュラはコマごとに変数の値が違うので、 札ごとの
 * 入札価格は違ってよい (backend が札ごとに保存時に計算する、 AC-BIN-020)。
 */
export type IntradayBulkCreatePrice = {
  price: Exclude<IntradayOrderCreatePrice, { kind: 'market' }>
}

/** 確定時に親へ渡す値。 */
export type IntradayBulkCreateConfirmedParams = IntradayBulkCreateParams &
  IntradayBulkCreatePrice

/** フォーミュラの、 作成対象の 1 コマのプレビュー (計算結果と、 そこから求めた入札価格の判定)。 */
export type IntradayBulkFormulaRowPreview = FormulaPricePreviewResult

/** 価格列の上に出す手数料自動考慮の案内 (i18n key は `bulkCreate.*` の末尾)。 */
export type IntradayBulkFormulaFeeHint = {
  key: FeeAutoConsiderHint['key'] | 'formulaFeeAdjusted' | 'formulaFeeOff'
  color: FeeAutoConsiderHint['color']
}

export type IntradayBulkFormulaSummary = {
  /** 確定を止める (手数料の確認が要る行か、 入札価格が範囲外の行がある)。 */
  isBlocking: boolean
  /** 価格列の上に出す案内。 OFF として扱う間を除き、 計算できた行がまだ無い間は出さない。 */
  feeHint: IntradayBulkFormulaFeeHint | undefined
  /** 入札価格が範囲外の最初の行の違反 (AC-BIN-104 の文言の元)。 */
  outOfRangeViolation: FeeAdjustmentViolation | undefined
}

/**
 * 作成対象のコマごとのフォーミュラのプレビューから、 確定を止めるかと案内を決める
 * (AC-BIN-004b / AC-BIN-005c)。 止めるかは札作成モーダルと同じ判定 (`formulaPriceSubmitCheck`) を
 * 行ごとに当て、 1 行でも止める行があれば確定しない。
 * 渡すのは作成対象の行だけ (スキップする行は作らないので判定に入れない)。
 */
export const summarizeIntradayBulkFormulaPreviews = (
  rows: readonly IntradayBulkFormulaRowPreview[],
  treatment: FeeAutoConsiderTreatment,
): IntradayBulkFormulaSummary => {
  const checks = rows.map(formulaPriceSubmitCheck)
  const feeBlocked = checks.find(
    (c): c is Extract<FormulaPriceSubmitCheck, { kind: 'feeBlocked' }> =>
      c.kind === 'feeBlocked',
  )
  const outOfRange = checks.find(
    (c): c is Extract<FormulaPriceSubmitCheck, { kind: 'outOfRange' }> =>
      c.kind === 'outOfRange',
  )
  const ready = rows
    .filter((r) => r.price.kind === 'calculated')
    .map((r) => r.fee)
    .find(
      (f): f is Extract<FeeAutoConsiderPreview, { kind: 'ready' }> =>
        f.kind === 'ready',
    )
  const feeHint = ((): IntradayBulkFormulaFeeHint | undefined => {
    if (feeBlocked !== undefined) {
      return feeAutoConsiderHint({ kind: 'blocked', reason: feeBlocked.reason })
    }
    if (treatment.status === 'fallbackOff') {
      return feeAutoConsiderHint(fallbackOffPreview(treatment.setting))
    }
    // 範囲外の行は手数料を加減算した結果なので ON。
    if (outOfRange !== undefined || ready?.confirmed.enabled === true) {
      return { key: 'formulaFeeAdjusted', color: 'Text.highEmphasis' }
    }
    if (ready !== undefined) {
      return { key: 'formulaFeeOff', color: 'Text.midEmphasis' }
    }
    return undefined
  })()
  return {
    isBlocking: checks.some((c) => c.kind !== 'ok'),
    feeHint,
    outOfRangeViolation: outOfRange?.violation,
  }
}

/**
 * フォーミュラの 1 コマの価格列 (AC-BIN-005c)。 計算できた行は入札価格 (手数料自動考慮が ON なら
 * 計算結果に加減算した値) を出す。 問い合わせ前・取得中・取得失敗は `none` (「—」)。
 */
export type IntradayBulkFormulaRowPrice =
  | { kind: 'none' }
  | { kind: 'unavailable' }
  | { kind: 'notCalculable' }
  | { kind: 'price'; orderPriceYenPerKwh: string; isOutOfRange: boolean }

export const intradayBulkFormulaRowPrice = (
  preview: IntradayBulkFormulaRowPreview | undefined,
): IntradayBulkFormulaRowPrice => {
  if (preview === undefined) return { kind: 'none' }
  const { price, fee } = preview
  switch (price.kind) {
    case 'pending':
    case 'failed':
      return { kind: 'none' }
    case 'variableUnavailable':
      return { kind: 'unavailable' }
    case 'notCalculable':
      return { kind: 'notCalculable' }
    case 'calculated':
      if (fee.kind === 'outOfRange') {
        return {
          kind: 'price',
          orderPriceYenPerKwh: fee.orderPriceYenPerKwh,
          isOutOfRange: true,
        }
      }
      return {
        kind: 'price',
        orderPriceYenPerKwh:
          fee.kind === 'ready' && fee.orderPriceYenPerKwh !== undefined
            ? fee.orderPriceYenPerKwh
            : formatYenPerKwh(price.formulaPriceYenPerKwh),
        isOutOfRange: false,
      }
  }
}

/**
 * 一括作成の確定結果。 `'feePreviewStale'` は手数料自動考慮の設定・単価・判定がプレビュー時から
 * 変わって拒否されたことを表し、 モーダルは Step1 に戻る (#9213)。
 */
export type IntradayBulkCreateOutcome = 'settled' | 'feePreviewStale'

/** 生成対象から外れた理由 (GC 通過済のコマだけ。 既存の札と重複する行は外さない)。 */
export type IntradayBulkSkipReason = 'gcPassed'

/** プレビュー 1 行 (= コマ 1 つ)。 `skipReason` 未設定の行のみが実際に作成される。 */
export type IntradayBulkPreviewRow = {
  timeCode: number
  skipReason?: IntradayBulkSkipReason
}

/**
 * 重複の判定に使う既存の札 (AC-BIN-006b)。 比べる相手は受渡日の自社の札のうち、 承認待ち・
 * 承認済・送信中・予約中・入札中のもので、 一覧の札から [[intradayBulkComparableBids]] が選ぶ。
 */
export type IntradayBulkExistingBid = {
  timeCode: number
  areaCode: AreaCode
  direction: Side
  /** 発注主体区分。 空欄 (UNSPECIFIED) の札は一括作成の行と一致しない。 */
  issuerType: LicenseType
  /** 価格種別。 一括作成は指値だけなので、 成行の札とは一致しない。 */
  isMarketOrder: boolean
  /** 入札価格 (円/kWh、 Decimal 文字列)。 */
  priceYenPerKwh: string
}

/**
 * 重複の警告で比べる相手の合成ステータス (AC-BIN-006b / スポット AC-BSP-114): 承認待ち・承認済・
 * 送信中・予約中・入札中。 送信中は一覧の合成ステータスの送信中 (JEPX の受け付け前。 送れるまで送り直して
 * いる札を含む、 #10680)、 承認済で送信前の札も送信中に含まれる (AC-BIN-035 / AC-BIN-051)。
 */
const COMPARABLE_STATUSES: ReadonlySet<IntradayTableStatus> = new Set([
  'pendingApproval',
  'sending',
  'reserved',
  'waiting',
])

/**
 * 一覧の札から、 重複の警告で比べる相手を選ぶ (AC-BIN-006b): 作成対象日の札のうち、 取下済でなく、
 * 合成ステータスが承認待ち・承認済・送信中・予約中・入札中のもの (取り込んだ札を含む)。
 * 一覧 RPC は受渡日で絞って引くが、 受渡日切替直後に前の受渡日の cache が残っても混ざらないよう、
 * ここでも受渡日で絞る。 価格を持たない指値は比べる相手にしない (成行は
 * [[intradayBulkDuplicateTimeCodes]] が外す)。
 */
export const intradayBulkComparableBids = (
  orders: readonly IntradayOrderRow[],
  targetDate: Date,
): IntradayBulkExistingBid[] =>
  orders.flatMap((o): IntradayBulkExistingBid[] => {
    if (
      isWithdrawnOrder(o) ||
      o.targetDate === undefined ||
      !isSameDay(fromProtoDate(o.targetDate), targetDate) ||
      !COMPARABLE_STATUSES.has(composeIntradayTableStatus(o)) ||
      o.priceYenPerKwh === undefined
    ) {
      return []
    }
    return [
      {
        timeCode: o.timeCode,
        areaCode: o.areaCode,
        direction: o.direction,
        issuerType: o.issuerType,
        isMarketOrder: isMarketOrderRow(o),
        priceYenPerKwh: o.priceYenPerKwh.value,
      },
    ]
  })

/**
 * コマ範囲を展開し、 コマごとに作成可否を判定した行を返す。
 *
 * - `timeCode < gcSlotIndex` は GC 通過済として skip する (= 呼び出し元の
 *   ribbon / chart のクリック不可コマと同じ境界。 design HTML の簡易式
 *   `slot + 2 < 現在コマ` ではなく、 画面内で一貫している GC 境界に揃える)。
 * - 既存の札との重複では skip しない (作成は止めず、 件数を警告する。
 *   [[intradayBulkDuplicateTimeCodes]])。
 */
export const buildIntradayBulkPreview = (
  params: IntradayBulkCreateParams,
  gcSlotIndex: number,
): IntradayBulkPreviewRow[] => {
  const from = Math.min(params.slotFrom, params.slotTo)
  const to = Math.max(params.slotFrom, params.slotTo)

  const rows: IntradayBulkPreviewRow[] = []
  for (let timeCode = from; timeCode <= to; timeCode++) {
    rows.push(
      timeCode < gcSlotIndex
        ? { timeCode, skipReason: 'gcPassed' }
        : { timeCode },
    )
  }
  return rows
}

/**
 * 作成する行のうち、 既存の札と重複するコマ番号 (AC-BIN-006b。 判定はスポット AC-BSP-114 の
 * 通常入札と同じ)。 受渡日・コマ・エリア・発注主体区分・売買・価格種別・入札価格がすべて一致する
 * 札があれば重複 (数量は比べない)。 一括作成は指値だけなので、 成行の札とは一致しない。
 *
 * @param orderPriceOf コマごとの入札価格 (手数料自動考慮が ON なら加減算後の、 実際に送る価格)。
 *   価格がまだ決まらないコマ (フォーミュラの計算前など) は undefined を返し、 重複にしない。
 */
export const intradayBulkDuplicateTimeCodes = (
  params: IntradayBulkCreateParams,
  existingBids: readonly IntradayBulkExistingBid[],
  targets: readonly number[],
  orderPriceOf: (timeCode: number) => string | undefined,
): number[] => {
  // 受渡日・状態は [[intradayBulkComparableBids]] が絞り済み。 発注主体区分が空欄の札は一致しない。
  const comparable = existingBids.filter(
    (bid) =>
      !bid.isMarketOrder &&
      bid.issuerType !== LicenseType.UNSPECIFIED &&
      bid.issuerType === params.issuerType &&
      bid.areaCode === params.areaCode &&
      bid.direction === params.direction,
  )
  return targets.filter((timeCode) => {
    const price = orderPriceOf(timeCode)
    if (price === undefined) return false
    return comparable.some(
      (bid) =>
        bid.timeCode === timeCode &&
        formatYenPerKwh(bid.priceYenPerKwh) === formatYenPerKwh(price),
    )
  })
}

/** 実際に作成されるコマ番号 (= skip されなかった行)。 */
export const intradayBulkTargets = (
  rows: readonly IntradayBulkPreviewRow[],
): number[] =>
  rows.filter((r) => r.skipReason === undefined).map((r) => r.timeCode)
